import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, ChevronRight, Pencil, Plus, Search } from "lucide-react";
import { useLanguage } from "@/core/LanguageContext";
import { useToast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/ui/dialog";
import { createSupplier, updateSupplier, type Supplier } from "../api";

interface SupplierFormData {
  name: string;
  code: string;
  contact_email: string;
}

const emptyForm: SupplierFormData = { name: "", code: "", contact_email: "" };

interface SuppliersTabProps {
  suppliers: Supplier[];
  suppliersQueryKey: readonly unknown[];
  tenantId: string;
  factoryId: string;
}

export function SuppliersTab({
  suppliers,
  suppliersQueryKey,
  tenantId,
  factoryId,
}: SuppliersTabProps) {
  const { t } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [supplierSearch, setSupplierSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplierId, setEditingSupplierId] = useState<string | null>(null);
  const [formData, setFormData] = useState<SupplierFormData>(emptyForm);

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingSupplierId(null);
    setFormData(emptyForm);
  };

  const openCreateDialog = () => {
    setEditingSupplierId(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (supplier: Supplier) => {
    setEditingSupplierId(supplier.id);
    setFormData({
      name: supplier.name,
      code: supplier.code,
      contact_email: supplier.contact_email || "",
    });
    setDialogOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editingSupplierId) {
        return updateSupplier(editingSupplierId, tenantId, factoryId, {
          name: formData.name,
          code: formData.code,
          contact_email: formData.contact_email || null,
        });
      }
      return createSupplier({
        tenant_id: tenantId,
        factory_id: factoryId,
        name: formData.name,
        code: formData.code,
        contact_email: formData.contact_email || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: suppliersQueryKey });
      toast({
        title: editingSupplierId
          ? t("mod_8d.supplierQuality.toast.supplierUpdated", "Supplier updated")
          : t("mod_8d.supplierQuality.toast.supplierCreated", "Supplier created"),
      });
      closeDialog();
    },
    onError: (error: Error) => {
      toast({
        title: t("mod_8d.supplierQuality.toast.errorTitle", "Error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const supplierList = suppliers
    .filter((supplier) => {
      if (!supplierSearch.trim()) return true;
      const term = supplierSearch.toLowerCase();
      return (
        supplier.name.toLowerCase().includes(term) ||
        supplier.code.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t(
              "mod_8d.supplierQuality.suppliers.searchPlaceholder",
              "Search suppliers...",
            )}
            value={supplierSearch}
            onChange={(e) => setSupplierSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            if (!open) closeDialog();
            else setDialogOpen(true);
          }}
        >
          <DialogTrigger asChild>
            <Button type="button" onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              {t("mod_8d.supplierQuality.actions.addSupplier", "Add Supplier")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingSupplierId
                  ? t("mod_8d.supplierQuality.actions.editSupplier", "Edit Supplier")
                  : t("mod_8d.supplierQuality.actions.addSupplier", "Add Supplier")}
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                saveMutation.mutate();
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="supplier-name">
                    {t("mod_8d.supplierQuality.form.companyName", "Company Name *")}
                  </Label>
                  <Input
                    id="supplier-name"
                    value={formData.name}
                    onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="supplier-code">
                    {t("mod_8d.supplierQuality.form.supplierCode", "Supplier Code *")}
                  </Label>
                  <Input
                    id="supplier-code"
                    value={formData.code}
                    onChange={(event) => setFormData({ ...formData, code: event.target.value })}
                    placeholder={t(
                      "mod_8d.supplierQuality.form.codePlaceholder",
                      "e.g., SUP001",
                    )}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplier-contact-email">
                  {t("mod_8d.supplierQuality.form.contactEmail", "Contact Email")}
                </Label>
                <Input
                  id="supplier-contact-email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(event) =>
                    setFormData({ ...formData, contact_email: event.target.value })
                  }
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={closeDialog}>
                  {t("mod_8d.supplierQuality.actions.cancel", "Cancel")}
                </Button>
                <Button type="submit" disabled={saveMutation.isPending}>
                  {editingSupplierId
                    ? t("mod_8d.supplierQuality.actions.update", "Update")
                    : t("mod_8d.supplierQuality.actions.create", "Create")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {supplierList.map((supplier) => (
          <Link key={supplier.id} to={`/supplier-quality/${supplier.id}`} className="block">
            <Card className="hover:border-primary/30 transition-colors">
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{supplier.name}</h3>
                      <p className="text-sm text-muted-foreground">{supplier.code}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        openEditDialog(supplier);
                      }}
                      aria-label={t(
                        "mod_8d.supplierQuality.actions.editSupplier",
                        "Edit Supplier",
                      )}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      {t("mod_8d.supplierQuality.suppliers.details", "Details")}
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}

        {supplierList.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {supplierSearch.trim()
                  ? t(
                      "mod_8d.supplierQuality.suppliers.emptySearch",
                      "No suppliers match your search",
                    )
                  : t(
                      "mod_8d.supplierQuality.suppliers.emptyFactory",
                      "No suppliers found for this factory",
                    )}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
